import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Boxes, ChevronRight, KeyRound, Plus, Server } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, EmptyState, Skeleton } from '../ui';
import { useMutation, useQuery } from '../gql/hooks';
import {
  CLIENT_QUERY,
  CREATE_APPLICATION_MUTATION,
  type ApplicationSummary,
  type ClientQueryResult,
  type CreateApplicationMutationResult,
  type CreateApplicationMutationVariables,
} from '../gql/operations';
import type { CreateApplicationInput } from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { ClientStatusBadge } from '../features/shared/StatusBadge';
import { ApplicationFormModal } from '../features/clients/ApplicationFormModal';
import { UsageTallyCard } from '../features/usage/UsageTallyCard';
import { formatDate } from '../lib/format';

// Client detail (US-8.1.1). Shows a tenant's applications; each drills into the keys view.

const ENV_TONE: Record<string, { dark: string; light: string }> = {
  PRODUCTION: {
    dark: 'border-indigo-500/20 bg-indigo-500/10 text-indigo-300',
    light: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  },
  STAGING: {
    dark: 'border-amber-500/20 bg-amber-500/10 text-amber-300',
    light: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  DEVELOPMENT: {
    dark: 'border-white/[0.1] bg-white/[0.04] text-white/60',
    light: 'border-slate-200 bg-slate-100 text-slate-600',
  },
};

function AppCard({
  app,
  clientId,
  delay,
}: {
  app: ApplicationSummary;
  clientId: string;
  delay: number;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const envTone = ENV_TONE[app.environment] ?? ENV_TONE.DEVELOPMENT;

  return (
    <Link
      to={`/clients/${clientId}/applications/${app.id}`}
      className={`group relative flex flex-col gap-3 overflow-hidden rounded-2xl border p-5 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02] hover:border-white/[0.12]'
          : 'border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:shadow-md'
      }`}
      style={{ animation: 'fadeInUp 0.6s ease-out forwards', animationDelay: `${delay}ms`, opacity: 0 }}
    >
      <div className="flex items-start justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/20">
          <Boxes className="h-5 w-5 text-white" aria-hidden="true" />
        </span>
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
            isDark ? envTone.dark : envTone.light
          }`}
        >
          {app.environment}
        </span>
      </div>
      <div>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {app.name}
        </h3>
        <p className={`mt-0.5 text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
          Created {formatDate(app.insertedAt)}
        </p>
      </div>
      <div
        className={`mt-1 flex items-center justify-between text-xs font-medium ${
          isDark ? 'text-white/50' : 'text-slate-500'
        }`}
      >
        <span className="inline-flex items-center gap-1.5">
          <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
          Manage keys
        </span>
        <ChevronRight
          className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
          aria-hidden="true"
        />
      </div>
    </Link>
  );
}

export function ClientDetailPage() {
  const { clientId = '' } = useParams();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { data, loading, error, refetch } = useQuery<ClientQueryResult, { id: string }>(
    CLIENT_QUERY,
    { id: clientId },
    { enabled: Boolean(clientId) },
  );
  const create = useMutation<
    CreateApplicationMutationResult,
    CreateApplicationMutationVariables
  >(CREATE_APPLICATION_MUTATION);

  const [formOpen, setFormOpen] = useState(false);

  const client = data?.client ?? null;
  const apps = client?.applications ?? [];

  const openCreate = () => {
    create.reset();
    setFormOpen(true);
  };
  const closeCreate = () => {
    setFormOpen(false);
    create.reset();
  };

  const handleSubmit = async (input: CreateApplicationInput) => {
    try {
      await create.mutate({ input });
      setFormOpen(false);
      create.reset();
      refetch();
    } catch {
      // Error surfaced inline in the modal via create.error; keep the form open to retry.
    }
  };

  const newAppButton = (
    <Button
      variant="primary"
      onClick={openCreate}
      leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
      disabled={!client}
    >
      New application
    </Button>
  );

  return (
    <div>
      <PageHeader
        title={client?.name ?? 'Client'}
        description="Applications belonging to this client. Open one to issue and manage its API keys."
        breadcrumbs={[
          { label: 'Clients', to: '/clients' },
          { label: client?.name ?? 'Client' },
        ]}
        actions={
          client ? (
            <div className="flex items-center gap-3">
              <ClientStatusBadge status={client.status} />
              {newAppButton}
            </div>
          ) : undefined
        }
      />

      {error && (
        <div
          role="alert"
          className={`mb-6 rounded-xl p-4 ${
            isDark ? 'border border-rose-500/20 bg-rose-500/10' : 'border border-rose-200 bg-rose-50'
          }`}
        >
          <p className={`text-sm ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>
            Couldn’t load this client: {error.message}
          </p>
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="apps-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="block" height={150} />
          ))}
        </div>
      )}

      {client && (
        <div className="mb-6">
          <UsageTallyCard
            filter={{ clientId: client.id }}
            title="Client usage"
            subtitle="Aggregated across all applications + API keys for this client"
          />
        </div>
      )}

      {!loading && !error && client && apps.length === 0 && (
        <EmptyState
          icon={<Server className="h-16 w-16" aria-hidden="true" />}
          title="No applications yet"
          description="This client has no applications. Applications group the API keys an integration uses."
          action={{ label: 'New application', onClick: openCreate }}
        />
      )}

      {!loading && apps.length > 0 && (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {apps.map((app, i) => (
            <li key={app.id}>
              <AppCard app={app} clientId={clientId} delay={(i + 1) * 80} />
            </li>
          ))}
        </ul>
      )}

      {client && (
        <ApplicationFormModal
          isOpen={formOpen}
          onClose={closeCreate}
          clientId={client.id}
          clientName={client.name}
          loading={create.loading}
          errorMessage={create.error?.message}
          onSubmit={(input) => void handleSubmit(input)}
        />
      )}
    </div>
  );
}
